const NEWLINE = "\n";
const ARRAY_OPEN = "[";
const ARRAY_CLOSE = "]";
const ARRAY_SEPARATOR = ", ";
const OBJECT_OPEN = "{";
const OBJECT_CLOSE = "}";
const OBJECT_KV = ": ";
const OBJECT_SEPARATOR = ",";

// set this to true for a narrower layout (complex map values are slightly indented below the key instead of behind the key)
const NARROW_LAYOUT = false;

/**
 * Tries to parse a string as json and does not throw on failure.
 * @param maybeJson
 * @returns a json object or undefined
 */
const safeParse = (maybeJson: string) => {
  try {
    return JSON.parse(maybeJson);
  } catch {
    return undefined;
  }
};

/**
 * Some of our data is not stored as proper json. This is a forgiving parser that tries to handle our
 * different cases.
 * @param maybeJson
 * @returns
 */
export const parseJsonLike = (maybeJson: string) => {
  let parsed = safeParse(maybeJson);
  if (parsed) {
    return parsed;
  }
  let modified;
  // we have lots of pseudo-json where we need to replace single quotes with double quotes
  // some strings are enclosed by double-backticks. We shoudn't replace single-quotes in those strings.
  const lines = maybeJson.split("``");
  if (lines.length % 2 == 1) {
    // every odd element is not a string enclosed by double-backticks
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 2) {
      lines[lineIndex] = lines[lineIndex].replaceAll("'", '"');
    }
    // this replaces double-backticks with double quotes
    modified = lines.join('"');
  } else {
    modified = maybeJson.replaceAll("'", '"');
  }

  parsed = safeParse(modified);
  if (parsed) {
    return parsed;
  }
  // we also sometimes have the Python `None` instead of the JS `null`. Try to catch those.
  // So that we don't replace random `None` values, we only go for object property values.
  modified = modified.replaceAll(/:\s*None(?=\W)/g, ": null");
  return safeParse(modified);
};

function isObject(obj: unknown) {
  return typeof obj === "object" && obj !== null;
}

function isArray(obj: unknown) {
  return Array.isArray(obj);
}

function indentValue(value: string, indent: number): string {
  const lines = value.split(NEWLINE);
  const indentValue = " ".repeat(indent);
  return indentValue + lines.join(NEWLINE + indentValue);
}

function indentAfter(prefix: string, value: string): string {
  const indented = indentValue(value, prefix.length);
  return prefix + indented.slice(prefix.length);
}

function indentBelow(prefix: string, value: string, indent: number): string {
  return prefix + NEWLINE + indentValue(value, indent);
}

/**
 * Concatenates strings with the ARRAY_SEPARATOR. Introduces newlines in
 * order to keep the result beneath a width of `maxWidth` characters.
 * @param values an arrays of strings
 * @param maxWidth
 * @returns
 */
function addWithMaxWidth(values: string[], maxWidth: number): string {
  let result = "";
  let line = "";
  for (const value of values) {
    if (line.length > 0) {
      line += ARRAY_SEPARATOR;
    }
    if (line.length + value.length < maxWidth) {
      line += value;
    } else {
      result += line + NEWLINE;
      line = value;
    }
  }
  if (line.length > 0) {
    result += line;
  }
  return result;
}

/**
 * Returns a pretty-printed JSON string representation of `obj`.
 * Objects are vertical (each key is on a new line).
 * Arrays of primitive values are horizontal, with a maxium width of 50 characters.
 * Arrays of objects are vertical (each element is on a new line)
 * Opening and closing brackets do not have their own lines.
 * Indentation is minimal.
 * Assumptions: object keys are primitives. An array has either only primitive values or no primitive values.
 * Implementation: render children separately, then indent and arrange them as required.
 * @param obj
 * @returns
 */
export const prettify = (obj: unknown): string => {
  if (!obj || (isArray(obj) && obj.length == 0)) {
    return JSON.stringify(obj);
  }
  if (isArray(obj)) {
    const children = obj.map((value) => prettify(value));
    if (isArray(obj[0]) || isObject(obj[0])) {
      return indentAfter(ARRAY_OPEN, children.join(OBJECT_SEPARATOR + NEWLINE)) + ARRAY_CLOSE;
    } else {
      return indentAfter(ARRAY_OPEN, addWithMaxWidth(children, 50)) + ARRAY_CLOSE;
    }
  }
  if (isObject(obj)) {
    const children = Object.entries(obj).map(([key, value]) => {
      const child = prettify(value);
      if (NARROW_LAYOUT && child.includes(NEWLINE)) {
        return prettify(key) + indentBelow(OBJECT_KV, child, 4);
      } else {
        return indentAfter(prettify(key) + OBJECT_KV, child);
      }
    });
    return indentAfter(OBJECT_OPEN, children.join(OBJECT_SEPARATOR + NEWLINE)) + OBJECT_CLOSE;
  }
  return JSON.stringify(obj);
};
